import React, { FunctionComponent, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import PATHS from 'app/utils/paths';

import NavBar from 'app/common/NavBar';
import Loader from 'app/common/Loader';
import SignIn from 'app/login/SignIn';
import Authenticated from 'app/login/Authenticated';
import { UserDataType, isAuthenticated, withAuth } from 'app/services/auth';
import { PrivateRoute, PublicRoute } from 'app/utils/routes';
import { theme } from 'app/theme';

import store from './storeConfig';

const Admin = React.lazy(() => import('app/admin'));
const AdminPeople = React.lazy(
  () => import('app/admin/components/subcomponents/AdminPeople')
);
const Activity = React.lazy(() => import('app/activity'));
const AssetsHub = React.lazy(() => import('app/assets'));
const Dashboard = React.lazy(() => import('app/dashboard'));
const Frameworks = React.lazy(() => import('app/frameworks'));
const Landing = React.lazy(() => import('app/landing/Landing'));
const NewAnalyze = React.lazy(() => import('app/analyze'));
const NewKPI = React.lazy(() => import('app/kpi'));
const ProjectsHub = React.lazy(() => import('app/projects'));
const ResourcesHub = React.lazy(() => import('app/resources'));
const RiskRegister = React.lazy(() => import('app/risk-register'));
const Search = React.lazy(() => import('app/search'));
const Workbench = React.lazy(() => import('app/workbench/Workbench'));
const Intro = React.lazy(() => import('app/knowledge-base/Intro'));
const XDay = React.lazy(() => import('app/knowledge-base/XDay'));
const ControlStatsFallacy = React.lazy(
  () => import('app/knowledge-base/ControlStatsFallacy')
);
const MeasurementThatMatters = React.lazy(
  () => import('app/knowledge-base/MeasurementThatMatters')
);
const MaturityScale = React.lazy(
  () => import('app/knowledge-base/MaturityScale')
);

interface AppProps {
  loggedIn: boolean;
  currentUser: UserDataType;
}

const ActivityRedirect = (): JSX.Element => {
  const location = useLocation();
  const nextPath = `${PATHS.CONNECTIONS}${location.pathname.slice(
    PATHS.ACTIVITY.length
  )}${location.search}${location.hash}`;

  return <Navigate to={nextPath} replace />;
};

const pathMatchesSection = (pathname: string, sectionPath: string): boolean =>
  pathname === sectionPath || pathname.startsWith(`${sectionPath}/`);

const PrivateAppSection = (): JSX.Element | null => {
  const location = useLocation();
  const { pathname } = location;

  if (pathMatchesSection(pathname, PATHS.KNOWLEDGE_BASE)) {
    return (
      <PrivateRoute>
        {pathname === PATHS.INTRO ? (
          <Intro />
        ) : pathname === PATHS.X_DAY ? (
          <XDay />
        ) : pathname === PATHS.CONTROL_STATS_FALLACY ? (
          <ControlStatsFallacy />
        ) : pathname === PATHS.MEASUREMENT_THAT_MATTERS ? (
          <MeasurementThatMatters />
        ) : pathname === PATHS.MATURITY_SCALE ? (
          <MaturityScale />
        ) : (
          <Navigate to={PATHS.INTRO} replace />
        )}
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.DASHBOARD)) {
    return (
      <PrivateRoute>
        <Dashboard />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.ANALYZE)) {
    return (
      <PrivateRoute>
        <NewAnalyze />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.KPI)) {
    return (
      <PrivateRoute>
        <NewKPI />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.CONNECTIONS)) {
    return (
      <PrivateRoute>
        <Activity />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.ACTIVITY)) {
    return (
      <PrivateRoute>
        <ActivityRedirect />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.RESOURCES)) {
    return (
      <PrivateRoute>
        <ResourcesHub />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.PEOPLE)) {
    return (
      <PrivateRoute>
        <AdminPeople />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.PROJECTS)) {
    return (
      <PrivateRoute>
        <ProjectsHub />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.ASSETS)) {
    return (
      <PrivateRoute>
        <AssetsHub />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.RISK_REGISTER)) {
    return (
      <PrivateRoute>
        <RiskRegister />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.FRAMEWORKS)) {
    return (
      <PrivateRoute>
        <Frameworks />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.WORKBENCH)) {
    return (
      <PrivateRoute>
        <Workbench />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.SEARCH)) {
    return (
      <PrivateRoute>
        <Search />
      </PrivateRoute>
    );
  }

  if (pathMatchesSection(pathname, PATHS.ADMIN)) {
    return (
      <PrivateRoute>
        <Admin />
      </PrivateRoute>
    );
  }

  return null;
};

const App: FunctionComponent<AppProps> = ({
  loggedIn,
  currentUser,
}: AppProps) => {
  return (
    <Provider store={store}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />

          <Router>
            {loggedIn && <NavBar currentUser={currentUser} />}
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route
                  path={PATHS.SIGNIN}
                  element={
                    <PublicRoute>
                      <SignIn />
                    </PublicRoute>
                  }
                />
                <Route path={PATHS.AUTHENTICATED} element={<Authenticated />} />
                <Route
                  path={PATHS.HOME}
                  element={
                    isAuthenticated() ? (
                      <Landing />
                    ) : (
                      <Navigate to={PATHS.SIGNIN} replace />
                    )
                  }
                />
                <Route path="*" element={<PrivateAppSection />} />
              </Routes>
            </Suspense>
          </Router>
        </ThemeProvider>
      </StyledEngineProvider>
    </Provider>
  );
};

export default withAuth(App);
